import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../../helpers/api_helper';
import { CalculationStatus, PackageStatus, UserPackages } from './types';
import {
  formatDecimalNumber,
  handleValidDate,
  handleValidTime,
  usdFormatter
} from '../../../helpers/helpers';

export const getTravelutionSubcriptions = createAsyncThunk(
  'getTravelutionSubcriptions/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/products/travelution-subscriptions/orders',
      {
        params
      }
    );

    return response.data.data;
  }
);

export const updateSubscriptionStatus = createAsyncThunk(
  'packageOrders/updateSubscriptionStatus',
  async (
    { id, calculationStatus }: { id: number; calculationStatus: number },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.post(
        `/products/travelution-subscriptions/orders/${id}/confirm?calculationStatus=${calculationStatus}`
      );
      toast.success('Successfully updated Travelution Subscription');

      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const editDateForAutostake = createAsyncThunk(
  'packageOrders/editDateForAutostake',
  async (
    { id, limitDate }: { id: number; limitDate: string | null },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        '/package/autostake/datelimit/edit',
        {
          userPackageId: id,
          limitDate
        }
      );
      toast.success(response.data.message);
      return response.data.data.userPackage as UserPackages;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const repeatTotalBonus = createAsyncThunk(
  'packageOrders/repeatTotalBonus',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/bonus/package/${id}/repeat`);
      toast.success(response.data.message);
      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const stornoPackageOrder = createAsyncThunk(
  'packageOrders/stornoPackageOrder',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('package/user/cancel', { id });
      toast.success(response.data.message);
      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const resendPaymentInstructions = createAsyncThunk(
  'packageOrders/resendPaymentInstructions',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put('/package/user/uncancel', {
        id
      });

      toast.success(response.data.message);
      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);
export const exportPackageOrders = createAsyncThunk(
  'packageOrders/exportPackageOrders',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      `/products/united-one-entry-packages/orders/export`,
      {
        params
      }
    );

    const dataForExport = response.data.data.orders.map((po: UserPackages) => ({
      orderId: po?.id,
      userId: po?.user?.id,
      user: `${po?.user?.name} ${po?.user?.surname}`,
      packageName: po?.package?.name,
      amount: po?.package?.price
        ? `$${formatDecimalNumber(po?.package?.price)}`
        : '',
      paymentAddress: po?.paymentAddress || '',
      orderDate: po?.dateCreated
        ? `${handleValidDate(po?.dateCreated)}, ${handleValidTime(
            po?.dateCreated
          )}`
        : '',
      paymentDate: po?.datePaid
        ? `${handleValidDate(po?.datePaid)}, ${handleValidTime(po?.datePaid)}`
        : '',

      status: statusForTable(po?.status, po?.calculationStatus)
    }));

    return dataForExport;
  }
);

export const exportHeaders = [
  { label: 'Order ID', key: 'orderId' },
  { label: 'User ID', key: 'userId' },
  { label: 'User', key: 'user' },
  { label: 'Package Name', key: 'packageName' },
  { label: 'Amount', key: 'amount' },
  { label: 'Payment Address', key: 'paymentAddress' },
  { label: 'Order Date', key: 'orderDate' },
  { label: 'Payment Date', key: 'paymentDate' },
  { label: 'Lock Date', key: 'lockDate' },
  { label: 'Status', key: 'status' }
];

export const statusForTable = (status: number, calculationStatus: number) => {
  switch (status) {
    case 1:
      return 'PENDING';
    case 2:
      return 'PAID';
    case 3:
      return 'CANCELED';
    case 4:
      return 'CANCELED BY USER';

    default:
      break;
  }
};

export const calculationStatusForTable = (status: number) => {
  switch (status) {
    case CalculationStatus.CALCULATE:
      return 'PAID';
    case CalculationStatus.SKIP_WITH_HUB_CREATION:
      return 'PAID WITH HUB NO CALCULATION NO POPV';
    case CalculationStatus.SKIP_WITHOUT_HUB_CREATION:
      return 'PAID NO HUB NO CALCULATION NO POPV';
    case CalculationStatus.SKIP_WITH_HUB_CREATION_ADD_PO_PV:
      return 'PAID WITH HUB NO CALC WITH POPV';
    case CalculationStatus.SKIP_WITHOUT_HUB_CREATION_ADD_PO_PV:
      return 'PAID NO HUB NO CALC WITH POPV';

    case CalculationStatus.SKIP_WITH_HUB_CREATION_ADD_PO:
      return 'PAID WITH HUB NO CALCULATION WITH PO';
    case CalculationStatus.SKIP_WITHOUT_HUB_CREATION_ADD_PO:
      return 'PAID NO HUB NO CALCULATION WITH PO';
    case CalculationStatus.SKIP_WITH_HUB_CREATION_ADD_PV:
      return 'PAID WITH HUB NO CALCULATION WITH PV';
    case CalculationStatus.SKIP_WITHOUT_HUB_CREATION_ADD_PV:
      return 'PAID NO HUB NO CALCULATION WITH PV';
    case CalculationStatus.LP_TRADING_ADD_PO:
      return 'PAID (LP) WITH HUB NO CALCULATION NO POPV';

    default:
      break;
  }
};

export interface PackageRankOrdersState {
  data: UserPackages[];
  loading: boolean;
  totalCount: number;
  dataForExport: UserPackages[];
  loadingExport: boolean;
}

const initialState = {
  data: [],
  totalCount: 0,
  loading: false,
  dataForExport: [],
  loadingExport: false
} as PackageRankOrdersState;

const packageOrdersSlice = createSlice({
  name: 'packageOrders',
  initialState,
  reducers: {
    removePackageOrdersForExport(state) {
      state.dataForExport = [];
    }
    // fill in primary logic here
  },
  extraReducers: (builder) => {
    //SUBSCRIPTIONS
    builder.addCase(getTravelutionSubcriptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getTravelutionSubcriptions.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.data = payload.orders;
        state.totalCount = payload.count;
      }
    );
    builder.addCase(getTravelutionSubcriptions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateSubscriptionStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      updateSubscriptionStatus.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        const index = state.data.findIndex((p) => p.id === payload);
        state.data[index] = {
          ...state.data[index],
          status: 2
        };
      }
    );
    builder.addCase(updateSubscriptionStatus.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(exportPackageOrders.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportPackageOrders.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportPackageOrders.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removePackageOrdersForExport } = packageOrdersSlice.actions;

export default packageOrdersSlice.reducer;
